import { memo, useEffect, useRef, useState } from 'react';
import { Card, CardContent, TextField, Typography, Grid, Box, Button, IconButton, Avatar, CardMedia, Tooltip, ToggleButtonGroup, ToggleButton, } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useLocation, useParams } from 'react-router-dom';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTMutation } from '../../../../../utils/useTMutation';
import showToast from '../../../../../components/Hot-Toast/showToast';
import CropperModal from '../../../../../components/Cropper/CropperModal';
import IMG from "../../../../../assets/images/big/profilebgimage.png";
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import { API_URL } from '../../../../../appConstants';
import { nextStep, update } from '../../../../../slice/multiStepSlice';

export const jobRoleSchema = z.object({
    ROLE_NAME: z.string().min(1, { message: "Job Role is required" }),
    licenseRequired: z.enum(["Yes", "No", "Optional"], {
        errorMap: () => ({ message: "You must select Yes or No" }),
    }),
    LICENSE: z.string().optional(),
    FILE: z.any()
        .refine((file) => {
            return file instanceof Blob || file !== "";
        }, { message: "File is required and must be an image" })
        .refine((file) => {
            return file !== "" ? true : file.size <= 5 * 1024 * 1024
        }, {
            message: "File size must be less than 5MB",
        })
        .refine((file) => {
            return file !== "" ? true : ['image/jpeg', 'image/png'].includes(file.type)
        }
            , {
                message: "Only JPEG and PNG files are allowed",
            }),
}).refine((data) => {
    // Only validate SPECILTY_ID if setAllRoles is false
    if (data.licenseRequired === 'Yes') {
        return data.LICENSE; // Ensure at least one specialty is selected
    }
    return true;
}, {
    message: "License Name is required",
    path: ["LICENSE"], // This is the field that the error message will be associated with
});

const AddJobRole = ({ data }) => {
    const dispatch = useDispatch();
    const { values } = useSelector((state) => state?.multiStep)
    const inputRef = useRef(null);
    const [src, setSrc] = useState(null);
    const [preview, setPreview] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [profileImageUrl, setProfileImageUrl] = useState(null);


    useEffect(() => {
        if (values?.IMAGE_NAME) {
            setProfileImageUrl(API_URL + "user/viewFile?fileName=" + values?.IMAGE_NAME);

        }
    }, [values?.IMAGE_NAME]);


    const handleInputClick = (e) => {
        e.preventDefault();
        inputRef.current.click();
    };
    const handleImgChange = (e) => {
        const file = e.target.files[0];
        if (file && file.size < 5 * 1024 * 1024) {
            setSrc(URL.createObjectURL(file));
            setModalOpen(true);
        } else {
            alert("Please select an image smaller than 5MB.");
        }
    };

    const handleCancel = (event, reason) => {
        if (reason && reason === 'backdropClick') return;
        setModalOpen(false);  // Close modal without changing the avatar
    };
    const {
        register,
        setValue,
        watch,
        handleSubmit,
        formState: { errors },
    } = useForm({
        resolver: zodResolver(jobRoleSchema),
        defaultValues: {
            ROLE_NAME: values.ROLE_NAME ? values.ROLE_NAME : "",
            FILE: values?.IMAGE_NAME ? values?.IMAGE_NAME : "",
            licenseRequired: (values?.LICENSE_REQUIRED) || "Optional",
            LICENSE: values?.LICENSE || "",
        },
    });
    console.log("values", values);

    const { mutateAsync } = useTMutation("/addUpdateJobRole", "post", (response) => {
        if (response.STATUS) {
            return response;
        }
    });



    const submitAddJobRole = async (data) => {

        try {
            const formData = new FormData();
            if (typeof data.FILE === "object") {
                formData.append("file", data.FILE, "roleIcon")
            }
            formData.append("ROLE_NAME", data?.ROLE_NAME);
            formData.append("LICENSE", data?.LICENSE);
            if (values?.ID) {
                formData.append("ID", values?.ID)
            }
            const response = await mutateAsync(formData);
            if (response.STATUS) {
                dispatch(update((values?.ID) ? (response.OUTPUT?.IMAGE_NAME) ? { ...data, IMAGE_NAME: response.OUTPUT.IMAGE_NAME, } : data : response.OUTPUT))
                dispatch(nextStep())
            } else {
                showToast("error", data.MESSAGE, 3000);
            }
        } catch (error) {
            console.error("Add Request failed:", error);
            showToast("error", "Something went wrong, Please try again", 3000);
        }
    };
    const handleToggleChange = (event, newValue) => {
        console.log("newValue", newValue);

        if (newValue !== null) {
            setValue("licenseRequired", newValue);
        }
    };
    return (
        <>
            <Grid lg={12} >
                <CardContent>
                    <Typography mt={-0.5} variant="h2" component="div" mb={1} fontWeight="bold">
                        Create Job Role
                    </Typography>
                    <form onSubmit={handleSubmit(submitAddJobRole)}>
                        <div style={{ position: 'relative' }}>
                            <Avatar
                                src={preview || profileImageUrl}
                                modalOpen={modalOpen}
                                setPreview={setPreview}
                                setModalOpen={setModalOpen}
                                sx={{

                                    borderRadius: 0,
                                    width: 120,
                                    height: 120,
                                    border: '3px solid white'
                                }}
                            />
                            <CameraAltOutlinedIcon
                                sx={{
                                    position: 'relative',
                                    top: -30,
                                    left: 95,
                                    padding: .5,
                                    height: 24,
                                    width: 24,
                                    backgroundColor: 'white',
                                    borderRadius: '50%',
                                    color: '#6ebe44',
                                    cursor: 'pointer'
                                }}
                                onClick={handleInputClick}
                            />
                            {errors?.FILE &&
                                <Typography color="error" variant="body2" mt={1}>
                                    {errors?.FILE?.message}
                                </Typography>
                            }
                            <input
                                type="file"
                                accept="image/*"
                                ref={inputRef}
                                onChange={handleImgChange}
                                hidden
                            />

                            {/* Ends of Profile Picture */}
                        </div>

                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            <TextField
                                label="Job Role"
                                variant="outlined"
                                fullWidth

                                margin="normal"
                                autoFocus={true}
                                inputProps={{ maxLength: 20 }}
                                // InputProps={{ style: { height: '50px' } }}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: 1.94
                                    },
                                    '& .MuiFormHelperText-root': {
                                        marginLeft: 0,
                                        marginTop: '4px',
                                    },
                                }}
                                {...register("ROLE_NAME")}
                                error={!!errors.ROLE_NAME}
                                helperText={errors.ROLE_NAME?.message}
                            />
                            <Box sx={{ display: "flex", alignItems: "center", marginTop: 2 }}>
                                <Typography variant="subtitle1" sx={{ marginRight: 1 }}>
                                    License Required for Job Role?
                                </Typography>
                                <Tooltip
                                    title="Yes: The License required during onboarding, clinicians must provide a valid license.
                                No: The license is not required for this job role. Clinicians are not required to provide a license during onboarding.
                                 ">
                                    <IconButton size="small">
                                        <InfoOutlinedIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            <Box sx={{ display: "flex", justifyContent: "start" }}>
                                <ToggleButtonGroup
                                    value={watch('licenseRequired')}
                                    exclusive
                                    onChange={handleToggleChange}
                                    aria-label="Toggle Type"
                                >
                                    <ToggleButton value="Optional" aria-label="Basic">
                                        Optional
                                    </ToggleButton>
                                    <ToggleButton value="Yes" aria-label="Basic">
                                        Yes
                                    </ToggleButton>
                                    <ToggleButton value="No" aria-label="Specific">
                                        No
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Box>
                            {watch('licenseRequired') === "Yes" && <TextField
                                label="License Name"
                                variant="outlined"
                                fullWidth
                                margin="normal"
                                autoFocus={true}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: 1.94
                                    },
                                    '& .MuiFormHelperText-root': {
                                        marginLeft: 0,
                                        marginTop: '2px',
                                    },
                                }}
                                {...register("LICENSE")}
                                error={!!errors.LICENSE}
                                helperText={errors.LICENSE?.message}
                            />}
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', }}>
                                <Button variant="contained" className='gradientAdminThemeColor' type='submit'>
                                    Next
                                </Button>
                            </Box>
                        </Box>
                        {modalOpen && (
                            <CropperModal
                                src={src}
                                modalOpen={modalOpen}
                                setModalOpen={setModalOpen}
                                setPreview={setPreview}
                                handleCancel={handleCancel}
                                onChange={(val) => {
                                    setValue("FILE", val);
                                    console.log("FILE", val);
                                }}
                                propsData={{ directory: "roleIcon", ID: values?.ID || "" }} 
                            />
                        )}
                    </form>
                </CardContent>
            </Grid>

        </>
    );
};

export default memo(AddJobRole);

